import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";


export const doctorsApi = createApi({
    reducerPath: "doctorsApi",
    baseQuery: fetchBaseQuery({
        baseUrl:`${process.env.NEXT_PUBLIC_BASE_URL}/doctors/`,
        prepareHeaders: (headers, { getState }) => {
            const cookies = document.cookie.split(";");
            let jwtCookie = null;

            cookies.forEach((cookie) => {
                const trimmedCookie = cookie.trim();
                if (trimmedCookie.startsWith("token=")) {
                    jwtCookie = "Bearer " + trimmedCookie.split("token=")[1];
                }
            });

            if (jwtCookie) {
                headers.set("authorization", jwtCookie);
            }

            return headers;
        },
    }),
    tagTypes: ['DoctorFavourites', 'DoctorFavouriteIds'],
    endpoints: (builder) => ({
        addDoctorFavourite: builder.mutation({
            query: (body) => ({
                url: `add-doctor-favourite`,
                method: "POST",
                body:body
            }),
            invalidatesTags: ['DoctorFavourites', 'DoctorFavouriteIds'],
        }),
        updateDoctorsFavourite: builder.mutation({
            query: ({id, ...body}) => ({
                url: `update-doctor-favourite/${id}`,
                method: "PUT",
                body:body
            }),
            invalidatesTags: ['DoctorFavourites'],
        }),
        updateDoctorsProfile: builder.mutation({
            query: (body) => ({
                url: `update-doctor-profile/${body?.id}`,
                method: "PUT",
                body:body
            }),
        }),
        updateDoctorsInfo: builder.mutation({
            query: ({id, ...body}) => ({
                url: `update-doctor-info/${id}`,
                method: "PUT",
                body:body
            }),
        }),
        updateDoctorsQualification: builder.mutation({
            query: ({id, ...body}) => ({
                url: `update-doctor-qualification/${id}`,
                method: "PUT",
                body:body
            }),
        }),

        updatePassword: builder.mutation({
            query: ({id, ...body}) => ({
                url: `update-password/${id}`,
                method: "PUT",
                body:body
            }),
        }),

        updateTwoFactor: builder.mutation({
            query: ({id, ...body}) => ({
                url: `update-two-factor/${id}`,
                method: "PUT",
                body:body
            }),
        }),
        removeDoctorFavourite: builder.mutation({
            query: (body) => ({
                url: `remove-doctor-favourite`,
                method: "DELETE",
                body:body
            }),
            invalidatesTags: ['DoctorFavourites', 'DoctorFavouriteIds'],
        }),
        // Move a graduated pipeline favourite to a regular medication
        // favourite. Backend handles the lookup of `graduatedTo`, creates
        // the product favourite (carrying notes over), and deletes the
        // pipeline favourite. We invalidate both cache tags so the
        // Favorites page and dashboard widget reflect the change.
        migratePipelineFavourite: builder.mutation({
            query: (body) => ({
                url: `migrate-pipeline-favourite`,
                method: "POST",
                body,
            }),
            invalidatesTags: ['DoctorFavourites', 'DoctorFavouriteIds'],
        }),

        updateProfileVisibility: builder.mutation({
            query: (body) => ({
                url: `update-doctor-visibility/${body.id}`,
                method: "PUT",
                body:body
            }),
        }),
        favouriteList: builder.query({
            query: () => ({
                url: `doctor-favourite`,
                method: "GET",
            }),
            providesTags: ['DoctorFavouriteIds'],
        }),
        getFavouriteDoctorsList: builder.query({
            query: (body) => {
                const params = new URLSearchParams();
                if (body?.id) params.set('id', body.id);
                if (body?.sortBy) params.set('sortBy', body.sortBy);
                if (body?.sortOrder) params.set('sortOrder', body.sortOrder);
                if (body?.page) params.set('page', String(body.page));
                if (body?.limit) params.set('limit', String(body.limit));
                return {
                    url: `doctor-favourite-list?${params.toString()}`,
                    method: "GET",
                };
            },
            providesTags: ['DoctorFavourites'],
            serializeQueryArgs: ({ queryArgs }) => {
                return `${queryArgs?.id}-${queryArgs?.sortBy}-${queryArgs?.sortOrder}`;
            },
            merge: (currentCache, newItems, { arg }) => {
                if (arg?.page === 1) {
                    return newItems;
                }
                return {
                    ...newItems,
                    data: [...(currentCache?.data || []), ...(newItems?.data || [])],
                };
            },
            forceRefetch: ({ currentArg, previousArg }) => {
                return currentArg?.page !== previousArg?.page
                    || currentArg?.sortBy !== previousArg?.sortBy
                    || currentArg?.sortOrder !== previousArg?.sortOrder;
            },
        }),
        getFavouriteDoctorsListIds: builder.query({
            query: (body) => ({
                url: `doctor-favourite-list-ids?id=${body?.id}${body?.sortBy ? '&sortBy=' + body.sortBy : ''}${body?.sortOrder ? '&sortOrder=' + body.sortOrder : ''}`,
                method: "GET",

            }),
            providesTags: ['DoctorFavouriteIds'],
        }),
    }),
})

export const  {
    useAddDoctorFavouriteMutation,
    useUpdateDoctorsFavouriteMutation,
    useUpdatePasswordMutation,
    useUpdateTwoFactorMutation,
    useUpdateDoctorsProfileMutation,
    useUpdateDoctorsInfoMutation,
    useUpdateDoctorsQualificationMutation,
    useRemoveDoctorFavouriteMutation,
    useMigratePipelineFavouriteMutation,
    useUpdateProfileVisibilityMutation,
    useFavouriteListQuery,
    useGetFavouriteDoctorsListQuery,
    useGetFavouriteDoctorsListIdsQuery,
} = doctorsApi
